/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import Imgix from 'js/components/Imgix';

import { Typography2 } from '@coursera/cds-core';
import type { PageConfigComponent as PageConfigComponentTrackingData } from '@coursera/event-pulse-types';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import Pill from 'bundles/unified-common/components/pill/Pill';

const styles = {
  pill: css`
    display: flex;
    white-space: nowrap;
    background-color: var(--cds-color-neutral-background-primary) !important;
    border: 1px solid var(--cds-color-grey-50) !important;
    gap: var(--cds-spacing-100) !important;
    transition: all 0.3s cubic-bezier(0, 0, 0.5, 1);
    transform: translateZ(0);

    &:hover {
      box-shadow: 0 0 8px -4px var(--cds-color-darken-300), 0 0 8px -6px var(--cds-color-darken-300);
      transform: scale3d(1.01, 1.01, 1.01) translate3d(1px, -2px, 16px);
      text-decoration: none;
      color: var(--cds-color-neutral-primary) !important;
      border: 1px solid var(--cds-color-neutral-stroke-primary-weak) !important;
    }
  `,
  logo: css`
    object-fit: contain;
  `,
};

export type PartnerLogoData = {
  name: string;
  logo: string;
  href: string;
};

type PartnerLogoProps = PartnerLogoData & {
  pageConfigComponentTrackingData?: PageConfigComponentTrackingData;
};

/**
 * Returns a clickable Pill component, displaying a partner logo and name.
 */
const PartnerLogo = ({ name, logo, href, pageConfigComponentTrackingData }: PartnerLogoProps) => {
  const track = useTracker();

  const partnerCardTrackingData = {
    partnerName: name,
    ...(pageConfigComponentTrackingData && { pageConfigComponent: pageConfigComponentTrackingData }),
  };

  const handleClick = () => {
    track('click_button', {
      button: {
        name: 'partner_logo',
        linkName: 'partner_logo_link',
        linkURL: href,
        linkType: 'internal',
        pageConfigComponent: pageConfigComponentTrackingData,
      },
      pageSection: {
        sectionName: 'partner_logo_list',
      },
    });

    track('click_partner_card', partnerCardTrackingData);
  };

  const visibilityTrack: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_partner_card',
    partnerCardTrackingData
  );

  return (
    <div ref={visibilityTrack}>
      <Pill href={href} css={styles.pill} onClick={handleClick}>
        <Imgix src={logo} alt="" width={24} height={24} maxWidth={24} maxHeight={24} css={styles.logo} />
        <Typography2 variant="subtitleSmall" component="span">
          {name}
        </Typography2>
      </Pill>
    </div>
  );
};

export default PartnerLogo;
